import { db } from '../firebase/firebaseConfig';
import { doc, setDoc, increment, serverTimestamp } from 'firebase/firestore';

const ACTION_COUNTERS_KEY = 'action_counters';
const MIN_PENDING_FOR_SYNC = 1; // Syncing frequently for now to ensure feedback, can be increased later

/**
 * Loads counters from local storage
 */
const loadLocalCounters = () => {
    try {
        const data = localStorage.getItem(ACTION_COUNTERS_KEY);
        return data ? JSON.parse(data) : {};
    } catch (e) {
        console.error('Error loading action counters:', e);
        return {};
    }
};

/**
 * Saves counters to local storage
 */
const saveLocalCounters = (counters) => {
    localStorage.setItem(ACTION_COUNTERS_KEY, JSON.stringify(counters));
};

/**
 * Increments a specific counter for a user locally and triggers sync if threshold met
 * @param {string} userId - The unique ID of the user
 * @param {string} counterName - The name of the counter to increment (e.g., 'commsioneditcounter', 'remindercounter')
 * @param {boolean} forceSync - Whether to force an immediate sync to Firestore
 */
export const incrementActionCounter = async (userId, counterName, forceSync = false) => {
    if (!userId || !counterName) {
        console.warn('incrementActionCounter: userId and counterName are required');
        return { success: false, message: 'Missing userId or counterName' };
    }

    const counters = loadLocalCounters();

    if (!counters[userId]) {
        counters[userId] = {};
    }

    if (!counters[userId][counterName]) {
        counters[userId][counterName] = 0;
    }

    // Increment locally
    counters[userId][counterName] += 1;
    saveLocalCounters(counters);

    // Calculate total pending for this user across all action counters
    const totalPending = Object.values(counters[userId]).reduce((a, b) => a + b, 0);

    if (forceSync || totalPending >= MIN_PENDING_FOR_SYNC) {
        return await syncActionCounters(userId);
    }

    return { success: true, message: 'Incremented locally' };
};

/**
 * Syncs all pending increments for a user to Firestore
 * @param {string} userId - The unique ID of the user
 */
export const syncActionCounters = async (userId) => {
    const counters = loadLocalCounters();

    if (!userId || !counters[userId]) {
        return { success: false, message: 'Nothing to sync' };
    }

    const userCounters = counters[userId];
    const updateData = {
        lastSyncedActions: serverTimestamp(),
        lastUpdated: serverTimestamp(),
    };

    let hasData = false;
    for (const [key, value] of Object.entries(userCounters)) {
        if (value > 0) {
            updateData[key] = increment(value);
            hasData = true;
        }
    }

    if (!hasData) {
        return { success: true, message: 'No pending counts' };
    }

    try {
        const counterRef = doc(db, 'upload_counters', userId);

        // Use setDoc with merge: true to create if not exists
        await setDoc(counterRef, updateData, { merge: true });

        // Reset local pending counts for this user after successful sync
        for (const key of Object.keys(userCounters)) {
            userCounters[key] = 0;
        }
        counters[userId] = userCounters;
        saveLocalCounters(counters);

        return { success: true };
    } catch (error) {
        console.error('Error syncing action counters to Firestore:', error);
        return { success: false, message: error.message };
    }
};

/**
 * Syncs actions for all users stored in local storage
 * Useful for cleanup or periodic syncing
 */
export const syncAllActionCounters = async () => {
    const counters = loadLocalCounters();
    const userIds = Object.keys(counters);
    const results = [];

    for (const userId of userIds) {
        const res = await syncActionCounters(userId);
        results.push({ userId, ...res });
    }

    return results;
};
